'use client';

import { type PropsWithChildren, type ReactNode, useEffect, useRef } from 'react';
import { TextCTA } from '../../atoms/text-cta/text-cta';
import { CrossS } from '../../icons/symbols/cross-s';
import styles from './modal.module.css';

type ModalBaseProps = PropsWithChildren<{
  footerContent: ReactNode;
  id: string;
  onClose?: () => void;
  title: string;
}>;

type ControlledModalProps = ModalBaseProps & {
  open: boolean;
  onClose: () => void;
};

type UncontrolledModalProps = ModalBaseProps & {
  open?: never;
};

export type ModalProps = ControlledModalProps | UncontrolledModalProps;

export const Modal = ({ children, footerContent, id, title, onClose, open }: ModalProps) => {
  const modalRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (!('commandForElement' in HTMLButtonElement.prototype)) {
      void import('invokers-polyfill/fn').then(({ apply }) => {
        apply();
      });
    }
  }, []);

  useEffect(() => {
    const dialog = modalRef.current;

    if (!dialog) {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      if (event.target === dialog) {
        dialog.close();
      }
    };

    dialog.addEventListener('click', handleClick);

    return () => {
      dialog.removeEventListener('click', handleClick);
    };
  }, []);

  useEffect(() => {
    const dialog = modalRef.current;

    if (!dialog) {
      return;
    }

    if (open && !dialog.open) {
      dialog.showModal();
    }

    if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  return (
    <dialog ref={modalRef} id={id} aria-label={title} className={styles.dialog} onClose={onClose} closedby="any">
      <div className={styles.header}>
        <TextCTA
          icon={<CrossS />}
          as="button"
          type="button"
          variant="default"
          command="close"
          commandFor={id}
          ariaLabel="Schließen"
        />
      </div>

      <div className={styles.content}>{children}</div>
      <footer className={styles.footer}>{footerContent}</footer>
    </dialog>
  );
};
